import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import type { ISubscriptionBlockApiPort } from '@subscriptions/application/ports';
import { extractErrorAttributes } from '@subscriptions/application/utils';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/**
 * Use case for generating the SPEI CLABE reference for a subscription block.
 *
 * Calls `POST /v1/subscription-block/{blockUuid}/generate-reference` and emits
 * a `generateBlockPaymentReference` event. Returns the full attached payment
 * method (CLABE in `providerPublicId`) so the confirmation screen can render
 * without refetching the block detail. The CLABE itself is never logged.
 *
 * @param port - Subscription block API port
 * @returns Use case executor
 */
export const generateReferenceSubscriptionBlockUseCase = (port: ISubscriptionBlockApiPort) => {
  const execute = async (blockUuid: string): Promise<PaymentMethodViewDto> => {
    const span = tracer.startSpan('SUB.generateBlockPaymentReference', {
      attributes: { 'block.uuid': blockUuid, methodType: 'SPEI' },
    });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const result = await port.generateReference(blockUuid);
        paymentLog.info('generateBlockPaymentReference', {
          'block.uuid': blockUuid,
          'payment.uuid': result.uuid,
          methodType: 'SPEI',
        });
        return result;
      } catch (error) {
        paymentLog.error('generateBlockPaymentReference', {
          'block.uuid': blockUuid,
          ...extractErrorAttributes(error),
        });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
